import { ThemeSpaceVariable, ToggleButton } from "@caspeco/casper-ui-library";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import ActivityTimeSlot from "components/selection-components/select-activity/activity-time-slot";
import { getConflictingArticle } from "components/selection-components/select-activity/get-conflicting-article";
import { useSectionNameMapping } from "components/selection-components/summary/use-section-name-mapping";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { Time } from "store/store-types";
import { TimeSlot } from "types/time-slot";

/**
 * Helper function to render a disabled ToggleButton with time and subtext
 *
 * @param timeDisplay - The time to display in HH:mm format
 * @param subText - The subtext to display (e.g., activity name or conflict message)
 * @param trackingId - Optional tracking ID for analytics
 * @param key - React key for the element
 * @returns A disabled ToggleButton element
 */
function renderTimeSlotButton(
    timeDisplay: moment.Moment,
    subText: string,
    trackingId?: string,
    key?: string
) {
    const timeString = timeDisplay.format("HH:mm");
    return (
        <ToggleButton
            key={key}
            aria-label={`${timeString} - ${subText}`}
            size="lg"
            h={ThemeSpaceVariable.XLarge}
            isDisabled
            data-track-click={trackingId}
            subText={subText}
            textAlign="center"
        >
            {timeString}
        </ToggleButton>
    );
}

interface ConflictAwareActivityTimeSlotProps {
    time: TimeSlot;
    webRuleId: number;
    selectedTime?: TimeSlot;
    onClick: (time: TimeSlot) => void;
    times: TimeSlot[];
    timesToNudge?: Time[];
    trackingId?: string;
    renderPaddingArticles?: boolean;
    /** Rule IDs of sibling activities in the same experience — excluded from conflict detection
     *  so that the user's own multi-activity booking doesn't block time slot selection. */
    ignoredRuleIds?: Set<number>;
    /** Start times (HH:mm) whose experience chain would conflict with other bookings. */
    chainConflictStartTimes?: Set<string>;
}

/**
 * Wrapper component that checks for conflicting articles and renders either:
 * - A disabled button with conflict info if there's a conflict
 * - A regular ActivityTimeSlot if no conflict exists
 *
 * @param time - The time slot to render
 * @param webRuleId - The ID of the web rule being edited (used to filter out self-conflicts)
 * @param selectedTime - The currently selected time slot
 * @param onClick - Callback when time slot is clicked
 * @param timesToNudge - Optional array of times to highlight with nudge indicator
 * @param times - All time slots for the web rule
 * @param trackingId - Optional tracking ID for analytics
 */
function ConflictAwareActivityTimeSlot({
    time,
    webRuleId,
    selectedTime,
    onClick,
    timesToNudge = [],
    times,
    trackingId,
    renderPaddingArticles = true,
    ignoredRuleIds,
    chainConflictStartTimes,
}: ConflictAwareActivityTimeSlotProps) {
    const data = useWebBooking().data;
    const sectionNameMap = useSectionNameMapping();
    const { t } = useTranslation();

    // Check if there's any collision conflict where THIS time slot is the closest start time
    // This takes precedence over time constraint conflicts on this slot
    const articles = data?.articles ?? [];
    const otherArticles = articles.filter(
        (a) => a.ruleId !== webRuleId && !ignoredRuleIds?.has(a.ruleId)
    );

    const isFirstTime = times[0]?.start.isSame(time.start);
    const isLastTime = times[times.length - 1]?.start.isSame(time.start);

    // Get articles that start before the first timeslot (these are padding articles before the available times)
    const bookedArticlesBeforeFirstTime = otherArticles.filter((article) =>
        article.start.isBefore(times[0].start)
    );

    // Get articles that start after the last timeslot (these are padding articles after the available times)
    const bookedArticlesAfterLastTime = otherArticles.filter((article) =>
        article.start.isAfter(times[times.length - 1].start)
    );

    const collisionToDisplay = otherArticles.find((article) => {
        // Check if any time slot would collide with this article
        const hasCollision = times.some((slot) => {
            return slot.start.isSameOrAfter(article.start) && slot.start.isBefore(article.end);
        });

        if (!hasCollision) return false;

        // Find the closest time slot to this article's start
        const closestSlot = times.find((timeSlot, index) => {
            return (
                article.start.isSameOrAfter(timeSlot.start) &&
                (index === times.length - 1 || article.start.isBefore(times[index + 1].start))
            );
        });

        // Check if this slot is the closest
        return closestSlot && closestSlot.start.isSame(time.start);
    });

    // Check for conflicts on this specific time slot (collision or time constraint)
    const conflict = getConflictingArticle(time, articles, webRuleId, ignoredRuleIds);

    // Determine what to render for the current time slot
    let currentTimeSlotElement: JSX.Element | null = null;

    if (collisionToDisplay) {
        // Show the collision article's actual start time
        const sectionName =
            sectionNameMap.get(collisionToDisplay.ruleId)?.name ||
            collisionToDisplay.articleGroupName;
        currentTimeSlotElement = renderTimeSlotButton(
            collisionToDisplay.start,
            sectionName,
            trackingId
        );
    } else if (conflict?.conflictReason === "collision") {
        // This slot collides but is NOT the closest to the collision - don't render
        currentTimeSlotElement = null;
    } else if (conflict?.conflictReason === "timeConstraint") {
        // Show time constraint message at the time slot's time
        const sectionName = t("conflictAwareActivityTimeSlot.timeConstraintTooltip");
        currentTimeSlotElement = renderTimeSlotButton(time.start, sectionName, trackingId);
    } else if (chainConflictStartTimes?.has(time.start.format("HH:mm"))) {
        // The experience chain starting at this time would conflict with another booking
        const sectionName = t("conflictAwareActivityTimeSlot.timeConstraintTooltip");
        currentTimeSlotElement = renderTimeSlotButton(time.start, sectionName, trackingId);
    } else {
        // No conflict - render normal time slot
        currentTimeSlotElement = (
            <ActivityTimeSlot
                time={time}
                selectedTime={selectedTime}
                onClick={() => onClick(time)}
                timesToNudge={timesToNudge}
                trackingId={trackingId}
                ignoredRuleIds={ignoredRuleIds}
            />
        );
    }

    // Determine padding articles to render
    const articlesToRenderBefore =
        renderPaddingArticles && isFirstTime ? bookedArticlesBeforeFirstTime : [];
    const articlesToRenderAfter =
        renderPaddingArticles && isLastTime ? bookedArticlesAfterLastTime : [];

    // If no additional elements, return just the current time slot element
    if (articlesToRenderBefore.length === 0 && articlesToRenderAfter.length === 0) {
        return currentTimeSlotElement;
    }

    // Render with padding articles
    return (
        <>
            {articlesToRenderBefore.map((article) => {
                const sectionName = sectionNameMap.get(article.ruleId)?.name || article.name;
                return renderTimeSlotButton(
                    article.start,
                    sectionName,
                    trackingId,
                    `padding-before-${article.start.format("HH:mm")}`
                );
            })}
            {currentTimeSlotElement}
            {articlesToRenderAfter.map((article) => {
                const sectionName = sectionNameMap.get(article.ruleId)?.name || article.name;
                return renderTimeSlotButton(
                    article.start,
                    sectionName,
                    trackingId,
                    `padding-after-${article.start.format("HH:mm")}`
                );
            })}
        </>
    );
}

export { ConflictAwareActivityTimeSlot };
